import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { broadcastApi, type StudentNotification } from "../../lib/broadcastApi";
import { ActionMenu } from "../ui/ActionMenu";

const SEEN_KEY = "soorgaa_notifications_seen";

function readSeen(): number {
  try {
    return Number(localStorage.getItem(SEEN_KEY)) || 0;
  } catch {
    return 0;
  }
}

/** Bell with a short "what is new" list. Notes only point to content; they never contain it. */
export function NotificationBell() {
  const { tokens } = useAuth();
  const token = tokens?.accessToken ?? "";
  const navigate = useNavigate();
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<StudentNotification[] | null>(null);
  const [seen, setSeen] = useState(readSeen);

  const load = useCallback(() => {
    if (!token) return;
    broadcastApi
      .notifications(token)
      .then((r) => setItems(r.items))
      .catch(() => setItems((prev) => prev ?? []));
  }, [token]);

  useEffect(() => {
    load();
    const id = window.setInterval(load, 5 * 60 * 1000);
    return () => window.clearInterval(id);
  }, [load]);

  const time = (n: StudentNotification) => (n.createdAt ? Date.parse(n.createdAt) : 0);
  const unread = (items ?? []).filter((n) => time(n) > seen).length;

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next && items?.length) {
      const latest = Math.max(...items.map(time));
      try {
        localStorage.setItem(SEEN_KEY, String(latest));
      } catch {
        // storage unavailable
      }
      setSeen(latest);
    }
  }

  return (
    <>
      <button
        ref={ref}
        type="button"
        onClick={toggle}
        aria-label={unread > 0 ? `Notifications, ${unread} new` : "Notifications"}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="relative flex h-11 w-11 items-center justify-center rounded-control text-muted-foreground hover:bg-secondary hover:text-foreground focus-visible:ring-2 ring-ring"
      >
        <Bell size={18} aria-hidden="true" />
        {unread > 0 && (
          <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-xs font-bold text-on-accent">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      <ActionMenu open={open} onClose={() => setOpen(false)} anchorRef={ref}>
        <div className="max-h-80 w-72 overflow-y-auto" role="dialog" aria-label="Notifications">
          {items === null ? (
            <p className="p-4 text-sm text-muted-foreground">Loading…</p>
          ) : items.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">Nothing new yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      if (n.link) navigate(n.link);
                    }}
                    className="block w-full px-4 py-3 text-left hover:bg-muted"
                  >
                    <span className="block text-sm font-semibold text-foreground">{n.title}</span>
                    {n.body && <span className="block truncate text-xs text-muted-foreground">{n.body}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </ActionMenu>
    </>
  );
}
